import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import React, { useState, useRef, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";

import { getAPIUrl } from "../../../config/api";
import { parseDate } from "../../../helpers/date";
import {
  useGetAllEdiConversionFilesQuery,
  useGetEdiConversionFileByIdQuery,
  useConvertAndUploadEdiMutation,
  useDeleteEdiConversionFileMutation,
  useReprocessEdiFileMutation,
  useRunAiAnalysisMutation,
} from "../../../store/queries/ediConversion";
import { useGetAllProvidersQuery } from "../../../store/queries/providers";
import { authSelector } from "../../../store/slices/auth";
import { userSelector } from "../../../store/slices/user";
import { IEdiConversionFile } from "../../../types/ediConversionFile";

const StyledDialog = styled(Dialog)`
  .p-dialog-content {
    padding: 1.5rem;
  }
`;

const FormField = styled.div`
  margin-bottom: 1rem;

  label {
    display: block;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #495057;
  }

  .required {
    color: #e74c3c;
    margin-left: 0.25rem;
  }
`;

const FileInfo = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 0.75rem 1rem;
  margin-top: 0.5rem;

  .file-name {
    font-weight: 600;
    margin-bottom: 0.25rem;
  }

  .file-details {
    color: #6c757d;
    font-size: 0.875rem;
  }
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
`;

function EdiConversionFilesTable() {
  const toast = useRef<Toast>(null);
  const { token } = useSelector(authSelector);
  const { currentAccount } = useSelector(userSelector);

  const [uploadDialogVisible, setUploadDialogVisible] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedFiles837, setSelectedFiles837] = useState<File[]>([]);
  const [providerId, setProviderId] = useState<string>("");
  const [searchValue, setSearchValue] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [pollingFileId, setPollingFileId] = useState<string | null>(null);

  const { data: ediConversionFilesData, isLoading } =
    useGetAllEdiConversionFilesQuery({
      search: searchValue,
    });
  const { data: providers } = useGetAllProvidersQuery();
  const { data: pollingData } = useGetEdiConversionFileByIdQuery(
    pollingFileId!,
    {
      skip: !pollingFileId,
      pollingInterval: 1000, // Poll every 1 second
    }
  );
  const [convertAndUpload] = useConvertAndUploadEdiMutation();
  const [deleteFile] = useDeleteEdiConversionFileMutation();
  const [reprocessFile] = useReprocessEdiFileMutation();
  const [runAiAnalysis] = useRunAiAnalysisMutation();

  const ediConversionFiles = ediConversionFilesData?.data || [];

  const providerOptions = useMemo(() => {
    if (!providers?.data) return [];
    return providers.data.map((p: any) => ({ label: p.name, value: p.id }));
  }, [providers]);

  // Monitor polling data and stop when processing is complete
  useEffect(() => {
    if (!pollingData?.data || !pollingFileId) return;

    const status = pollingData.data.claimProcessingStatus;
    const terminalStatuses = ["completed", "failed", "skipped"];

    if (status && terminalStatuses.includes(status)) {
      // Stop polling
      setPollingFileId(null);

      // Show appropriate toast message
      if (status === "completed") {
        toast.current?.show({
          severity: "success",
          summary: "Processing Complete",
          detail: "Underpayment records have been successfully created",
        });
      } else if (status === "failed") {
        toast.current?.show({
          severity: "error",
          summary: "Processing Failed",
          detail: "Failed to create underpayment records",
        });
      } else if (status === "skipped") {
        toast.current?.show({
          severity: "info",
          summary: "Processing Skipped",
          detail:
            "Underpayment processing was skipped (no provider ID specified)",
        });
      }
    }
  }, [pollingData, pollingFileId]);

  const resetForm = () => {
    setDisplayName("");
    setDescription("");
    setSelectedFiles([]);
    setSelectedFiles837([]);
    setProviderId("");
  };

  const handleFileSelect = (event: any) => {
    const newFiles = Array.from(event.files) as File[];
    setSelectedFiles((prev) => {
      const existing = new Set(prev.map((f) => `${f.name}-${f.size}`));
      const unique = newFiles.filter(
        (f) => !existing.has(`${f.name}-${f.size}`)
      );
      return [...prev, ...unique];
    });
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFile837Select = (event: any) => {
    const newFiles = Array.from(event.files) as File[];
    setSelectedFiles837((prev) => {
      const existing = new Set(prev.map((f) => `${f.name}-${f.size}`));
      const unique = newFiles.filter(
        (f) => !existing.has(`${f.name}-${f.size}`)
      );
      return [...prev, ...unique];
    });
  };

  const handleRemoveFile837 = (index: number) => {
    setSelectedFiles837((prev) => prev.filter((_, i) => i !== index));
  };

  const totalFileSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);
  const totalFile837Size = selectedFiles837.reduce((sum, f) => sum + f.size, 0);

  const handleUpload = async () => {
    if (!displayName.trim() || selectedFiles.length === 0) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please provide a display name and select at least one file",
      });
      return;
    }

    setIsUploading(true);
    try {
      const result = await convertAndUpload({
        files: selectedFiles,
        displayName: displayName.trim(),
        description: description.trim() || undefined,
        providerId: providerId || undefined,
        files837: selectedFiles837.length > 0 ? selectedFiles837 : undefined,
      }).unwrap();

      // Show warnings from the server if any files were skipped or had issues
      if (result.warnings && result.warnings.length > 0) {
        toast.current?.show({
          severity: "warn",
          summary: "Upload Warnings",
          detail: result.warnings.join("; "),
          life: 8000,
        });
      }

      // Show initial toast indicating processing has started
      toast.current?.show({
        severity: "info",
        summary: "Processing Started",
        detail: "EDI file uploaded successfully. Processing underpayments...",
      });

      // Start polling the file status
      setPollingFileId(result.data.id);

      resetForm();
      setUploadDialogVisible(false);
    } catch (error: any) {
      console.error("Upload error:", error);
      toast.current?.show({
        severity: "error",
        summary: "Upload Failed",
        detail: error?.data?.message || "Failed to convert and upload EDI file",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async (id: string) => {
    try {
      const apiUrl = getAPIUrl();
      const headers: HeadersInit = {
        Authorization: `Bearer ${token}`,
      };

      if (currentAccount) {
        headers.account = currentAccount;
      }

      const response = await fetch(
        `${apiUrl}${
          apiUrl.endsWith("/") ? "" : "/"
        }edi-conversion/${id}/download`,
        {
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();

      // URL is nested in data.data.downloadUrl based on backend response structure
      const downloadUrl =
        data?.data?.downloadUrl || data?.downloadUrl || data?.url;

      if (downloadUrl) {
        window.open(downloadUrl, "_blank");
      } else {
        throw new Error("No download URL in response");
      }
    } catch (error: any) {
      console.error("Download error:", error);
      toast.current?.show({
        severity: "error",
        summary: "Download Failed",
        detail: error?.message || "Failed to download file",
      });
    }
  };

  const handleDownload837 = async (id: string) => {
    try {
      const apiUrl = getAPIUrl();
      const headers: HeadersInit = {
        Authorization: `Bearer ${token}`,
      };

      if (currentAccount) {
        headers.account = currentAccount;
      }

      const response = await fetch(
        `${apiUrl}${
          apiUrl.endsWith("/") ? "" : "/"
        }edi-conversion/${id}/download-837`,
        {
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();

      const downloadUrl =
        data?.data?.downloadUrl || data?.downloadUrl || data?.url;

      if (downloadUrl) {
        window.open(downloadUrl, "_blank");
      } else {
        throw new Error("No download URL in response");
      }
    } catch (error: any) {
      console.error("Download 837 error:", error);
      toast.current?.show({
        severity: "error",
        summary: "Download Failed",
        detail: error?.message || "Failed to download 837 file",
      });
    }
  };

  const handleDelete = async (id: string, displayName: string) => {
    confirmDialog({
      message: `Are you sure you want to delete "${displayName}"?`,
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        try {
          await deleteFile(id).unwrap();
          toast.current?.show({
            severity: "success",
            summary: "Deleted",
            detail: "EDI conversion file deleted successfully",
          });
        } catch (error: any) {
          console.error("Delete error:", error);
          toast.current?.show({
            severity: "error",
            summary: "Delete Failed",
            detail: error?.data?.message || "Failed to delete file",
          });
        }
      },
    });
  };

  const handleReprocess = async (id: string, displayName: string) => {
    confirmDialog({
      message: `Are you sure you want to reprocess "${displayName}"? This will delete all existing claims and regenerate them with current payer mappings and fee schedules.`,
      header: "Confirm Reprocess",
      icon: "pi pi-refresh",
      accept: async () => {
        try {
          await reprocessFile(id).unwrap();
          toast.current?.show({
            severity: "info",
            summary: "Reprocessing Started",
            detail:
              "EDI file is being reprocessed. Claims will be regenerated.",
          });
          // Start polling to monitor progress
          setPollingFileId(id);
        } catch (error: any) {
          console.error("Reprocess error:", error);
          toast.current?.show({
            severity: "error",
            summary: "Reprocess Failed",
            detail: error?.data?.message || "Failed to reprocess file",
          });
        }
      },
    });
  };

  const handleRunAiAnalysis = async (id: string, displayName: string) => {
    confirmDialog({
      message: `Are you sure you want to run AI analysis for all claims in "${displayName}"? This will overwrite any existing AI analysis results.`,
      header: "Confirm AI Analysis",
      icon: "pi pi-bolt",
      accept: async () => {
        try {
          const result = await runAiAnalysis(id).unwrap();
          toast.current?.show({
            severity: "info",
            summary: "AI Analysis Started",
            detail: `AI analysis started for ${result.data.claimCount} claims. This may take a few minutes.`,
          });
        } catch (error: any) {
          console.error("AI analysis error:", error);
          toast.current?.show({
            severity: "error",
            summary: "AI Analysis Failed",
            detail: error?.data?.message || "Failed to start AI analysis",
          });
        }
      },
    });
  };

  const dateBodyTemplate = (rowData: IEdiConversionFile) => {
    return parseDate(rowData.dateConverted);
  };

  const recordCountBodyTemplate = (rowData: IEdiConversionFile) => {
    return rowData.recordCount.toLocaleString();
  };

  const convertedByBodyTemplate = (rowData: IEdiConversionFile) => {
    // Handle if convertedBy is an object with name property
    if (
      typeof rowData.convertedBy === "object" &&
      rowData.convertedBy !== null
    ) {
      return (
        (rowData.convertedBy as any).name ||
        (rowData.convertedBy as any).email ||
        "Unknown"
      );
    }
    return rowData.convertedBy || "Unknown";
  };

  const providerBodyTemplate = (rowData: IEdiConversionFile) => {
    if (!rowData.providerId) {
      return <span className="text-500">-</span>;
    }

    // Find provider name from the list
    const provider = providers?.data?.find(
      (p: any) => p.id === rowData.providerId
    );
    return provider?.name || rowData.providerId;
  };

  const statusBodyTemplate = (rowData: IEdiConversionFile) => {
    const status = rowData.claimProcessingStatus;
    const isPolling = pollingFileId === rowData.id;

    if (!status) {
      return <span className="text-500">-</span>;
    }

    switch (status) {
      case "pending":
        return (
          <span
            style={{
              color: "#6c757d",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            {isPolling && (
              <i
                className="pi pi-spin pi-spinner"
                style={{ fontSize: "1rem" }}
              />
            )}
            <span>Pending</span>
          </span>
        );
      case "processing":
        return (
          <span
            style={{
              color: "#007bff",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <i className="pi pi-spin pi-spinner" style={{ fontSize: "1rem" }} />
            <span>Processing</span>
          </span>
        );
      case "completed":
        return (
          <span style={{ color: "#28a745" }}>
            <i
              className="pi pi-check-circle"
              style={{ fontSize: "1rem", marginRight: "0.25rem" }}
            />
            Completed
          </span>
        );
      case "failed":
        return (
          <span style={{ color: "#dc3545" }}>
            <i
              className="pi pi-times-circle"
              style={{ fontSize: "1rem", marginRight: "0.25rem" }}
            />
            Failed
          </span>
        );
      case "skipped":
        return (
          <span style={{ color: "#ffc107" }}>
            <i
              className="pi pi-info-circle"
              style={{ fontSize: "1rem", marginRight: "0.25rem" }}
            />
            Skipped
          </span>
        );
      default:
        return <span>{status}</span>;
    }
  };

  const actionsBodyTemplate = (rowData: IEdiConversionFile) => {
    const status = rowData.claimProcessingStatus;
    const canReprocess =
      rowData.providerId && (status === "completed" || status === "failed");
    const isProcessing = status === "processing" || status === "pending";

    return (
      <div style={{ display: "flex", gap: "0.5rem" }}>
        {canReprocess && (
          <Button
            icon="pi pi-refresh"
            className="p-button-rounded p-button-text p-button-warning"
            onClick={() => handleReprocess(rowData.id, rowData.displayName)}
            tooltip="Reprocess claims"
            disabled={isProcessing}
          />
        )}
        {status === "completed" && (
          <Button
            icon="pi pi-bolt"
            className="p-button-rounded p-button-text p-button-help"
            onClick={() => handleRunAiAnalysis(rowData.id, rowData.displayName)}
            tooltip="Run AI analysis"
          />
        )}
        <Button
          icon="pi pi-download"
          className="p-button-rounded p-button-text"
          onClick={() => handleDownload(rowData.id)}
          tooltip="Download 835 CSV"
        />
        {rowData.has837 && (
          <Button
            icon="pi pi-download"
            className="p-button-rounded p-button-text p-button-info"
            onClick={() => handleDownload837(rowData.id)}
            tooltip="Download 837 CSV"
          />
        )}
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger"
          onClick={() => handleDelete(rowData.id, rowData.displayName)}
          tooltip="Delete"
        />
      </div>
    );
  };

  const dialogFooter = (
    <div>
      <Button
        label="Cancel"
        icon="pi pi-times"
        className="p-button-text"
        onClick={() => {
          resetForm();
          setUploadDialogVisible(false);
        }}
        disabled={isUploading}
      />
      <Button
        label={isUploading ? "Converting..." : "Convert & Upload"}
        icon={isUploading ? "pi pi-spin pi-spinner" : "pi pi-upload"}
        onClick={handleUpload}
        disabled={
          isUploading || selectedFiles.length === 0 || !displayName.trim()
        }
      />
    </div>
  );

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <Toast ref={toast} />

      <div className="flex justify-content-between align-items-center mb-3">
        <div className="flex align-items-center">
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search files..."
              className="w-20rem"
            />
          </span>
        </div>
        <Button
          label="Upload & Convert EDI File"
          icon="pi pi-plus"
          className="p-button-raised"
          onClick={() => setUploadDialogVisible(true)}
        />
      </div>

      <DataTable
        value={ediConversionFiles}
        paginator
        rows={10}
        stripedRows
        responsiveLayout="scroll"
        emptyMessage="No EDI conversion files found."
      >
        <Column
          field="providerId"
          header="Provider"
          sortable
          body={providerBodyTemplate}
          style={{ minWidth: "10rem" }}
        />
        <Column
          field="displayName"
          header="Display Name"
          sortable
          style={{ minWidth: "12rem" }}
        />
        <Column
          field="fileName"
          header="File Name"
          sortable
          style={{ minWidth: "12rem" }}
        />
        <Column
          field="convertedBy"
          header="Converted By"
          sortable
          body={convertedByBodyTemplate}
          style={{ minWidth: "10rem" }}
        />
        <Column
          field="dateConverted"
          header="Date Converted"
          sortable
          body={dateBodyTemplate}
          style={{ minWidth: "10rem" }}
        />
        <Column
          field="recordCount"
          header="Record Count"
          sortable
          body={recordCountBodyTemplate}
          style={{ minWidth: "8rem" }}
        />
        <Column
          field="claimProcessingStatus"
          header="Status"
          sortable
          body={statusBodyTemplate}
          style={{ minWidth: "10rem" }}
        />
        <Column
          body={actionsBodyTemplate}
          header="Actions"
          style={{ minWidth: "8rem" }}
        />
      </DataTable>

      <StyledDialog
        header="Upload & Convert EDI File"
        visible={uploadDialogVisible}
        onHide={() => {
          if (!isUploading) {
            resetForm();
            setUploadDialogVisible(false);
          }
        }}
        footer={dialogFooter}
        style={{ width: "500px" }}
        modal
        closable={!isUploading}
        blockScroll
      >
        <FormField>
          <label>
            Display Name <span className="required">*</span>
          </label>
          <InputText
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g., Bluecross Claims Batch A"
            className="w-full"
            disabled={isUploading}
          />
        </FormField>

        <FormField>
          <label>Description</label>
          <InputTextarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description..."
            rows={3}
            className="w-full"
            disabled={isUploading}
          />
        </FormField>

        <FormField className="mb-4">
          <label>Provider</label>
          <Dropdown
            value={providerId}
            options={providerOptions}
            onChange={(e) => setProviderId(e.value)}
            placeholder="Select Provider (Optional)"
            className="w-full"
            filter
            showClear
            disabled={isUploading}
            appendTo="self"
          />
        </FormField>

        <FormField>
          <label>
            835 Remittance Files (.txt, .edi, .835, .zip){" "}
            <span className="required">*</span>
          </label>
          {selectedFiles.length > 0 && (
            <FileList>
              {selectedFiles.map((file, index) => (
                <FileInfo key={`${file.name}-${index}`}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <div className="file-name">{file.name}</div>
                      <div className="file-details">
                        {(file.size / 1024).toFixed(1)} KB
                      </div>
                    </div>
                    <Button
                      icon="pi pi-times"
                      className="p-button-text p-button-sm p-button-danger"
                      onClick={() => handleRemoveFile(index)}
                      disabled={isUploading}
                      tooltip="Remove file"
                    />
                  </div>
                </FileInfo>
              ))}
              <div
                style={{
                  fontSize: "0.85rem",
                  color: "#6c757d",
                  marginTop: "0.25rem",
                }}
              >
                {selectedFiles.length} file
                {selectedFiles.length !== 1 ? "s" : ""} selected (
                {(totalFileSize / 1024).toFixed(1)} KB total)
              </div>
            </FileList>
          )}
          <FileUpload
            mode="basic"
            name="files"
            accept=".txt,.edi,.835,.zip"
            maxFileSize={30000000}
            chooseLabel={
              selectedFiles.length > 0 ? "Add More Files" : "Choose 835 Files"
            }
            className="w-full"
            onSelect={handleFileSelect}
            disabled={isUploading}
            multiple
          />
          <small className="text-muted">
            Supported formats: .txt, .edi, .835, .zip (Max total size: 30MB)
          </small>
        </FormField>

        <FormField>
          <label>837 Claim Files (Optional)</label>
          {selectedFiles837.length > 0 && (
            <FileList>
              {selectedFiles837.map((file, index) => (
                <FileInfo key={`837-${file.name}-${index}`}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <div className="file-name">{file.name}</div>
                      <div className="file-details">
                        {(file.size / 1024).toFixed(1)} KB
                      </div>
                    </div>
                    <Button
                      icon="pi pi-times"
                      className="p-button-text p-button-sm p-button-danger"
                      onClick={() => handleRemoveFile837(index)}
                      disabled={isUploading}
                      tooltip="Remove file"
                    />
                  </div>
                </FileInfo>
              ))}
              <div
                style={{
                  fontSize: "0.85rem",
                  color: "#6c757d",
                  marginTop: "0.25rem",
                }}
              >
                {selectedFiles837.length} file
                {selectedFiles837.length !== 1 ? "s" : ""} selected (
                {(totalFile837Size / 1024).toFixed(1)} KB total)
              </div>
            </FileList>
          )}
          <FileUpload
            mode="basic"
            name="files837"
            accept=".txt,.edi,.837,.zip"
            maxFileSize={30000000}
            chooseLabel={
              selectedFiles837.length > 0
                ? "Add More 837 Files"
                : "Choose 837 Files"
            }
            className="w-full"
            onSelect={handleFile837Select}
            disabled={isUploading}
            multiple
          />
          <small className="text-muted">
            Optional: Upload matching 837 claim files for inpatient/outpatient
            classification (.txt, .edi, .837, .zip)
          </small>
        </FormField>
      </StyledDialog>
    </>
  );
}

export default EdiConversionFilesTable;
